import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { Box, Paper, Alert } from '@mui/material'
import { LoadingIndicator } from '@/apps/legacy/components/atoms/LoadingIndicator'
import TabSwitcher from '@/apps/legacy/components/portal/TabSwitcher'
import SearchBox from '@/apps/legacy/components/portal/SearchBox'
import PaginationControl from '@/apps/legacy/components/portal/PaginationControl'
import DrugGroupTable from '@/apps/legacy/components/portal/DrugGroupTable'
import EnhancedDrugTable from '@/apps/legacy/components/portal/EnhancedDrugTable'
import {
  useDrugGroupsQuery,
  useDrugSearchQuery,
} from '@/features/internal/drugs'
import { useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/shared/hooks/queries/queryKeys'
import { useLocation, useNavigate } from 'react-router-dom'
import { AiGenerationRequest } from '@/features/internal/drugs/types/soapTemplate'
import AiSoapTemplateGenerationModal from '@/components/molecules/AiSoapTemplateGenerationModal'
import DuplicateConfirmationDialog from '@/components/molecules/DuplicateConfirmationDialog'
import { useToast } from '@/shared/contexts/ToastContext'
import { useAiSoapTemplateGeneration } from '@/features/internal/drugs'
import { AdvancedSearchCondition } from '@/shared/types/advancedSearch'
import { PortalLayout } from '@/apps/legacy/components/portal/PortalLayout'
import { PortalPageHeader } from '@/apps/legacy/components/portal/PortalPageHeader'

// 決定的なID生成のための関数（URLパラメータ復元用）
const generateDeterministicId = (
  type: string,
  value: string,
  index: number
) => {
  return `${type}-${value}-${index}`.replace(/[^a-zA-Z0-9-]/g, '-')
}

// URLパラメータから高度検索条件を復元するヘルパー関数
const parseAdvancedSearchFromUrl = (
  searchParam: string
): {
  isAdvanced: boolean
  conditions: AdvancedSearchCondition[]
} => {
  if (!searchParam.startsWith('[高度]')) {
    return { isAdvanced: false, conditions: [] }
  }

  const conditionsString = searchParam.replace('[高度]', '')
  const conditionPairs = conditionsString.split('+')

  const conditions: AdvancedSearchCondition[] = conditionPairs
    .map((pair, index) => {
      const [type, value] = pair.split(':')
      if (!type || !value) return null

      // typeからfieldを決定
      const fieldMap: Record<string, string> = {
        yj_code: 'yj_code',
        drug_name: 'product_name',
        ingredient: 'general_name',
        dosage_form: 'dosage_form',
      }

      return {
        id: generateDeterministicId(type, value, index),
        type: type as AdvancedSearchCondition['type'],
        field: fieldMap[type] || 'product_name',
        value: decodeURIComponent(value),
        operator: 'and' as const,
      }
    })
    .filter(Boolean) as AdvancedSearchCondition[]

  return { isAdvanced: true, conditions }
}

/**
 * SOAPテンプレート一覧画面
 */
const SoapTemplateListPage: React.FC = () => {
  // URLパラメータからタブを取得
  const location = useLocation()
  const navigate = useNavigate()
  const searchParams = new URLSearchParams(location.search)
  const tabParam = searchParams.get('tab')

  // 状態管理
  const [activeTab, setActiveTab] = useState(tabParam === 'drugs' ? 1 : 0)
  const [searchKeyword, setSearchKeyword] = useState(
    searchParams.get('search') || ''
  )
  const [page, setPage] = useState(
    searchParams.get('page') ? parseInt(searchParams.get('page') || '1', 10) : 1
  )

  // レガシー状態管理を削除 - 高度検索フックに統一

  // AI生成機能
  const {
    state: aiState,
    isLoading: aiGenerating,
    isDuplicateDetected,
    executeGeneration,
    executeMerge,
    executeRename,
    cancelGeneration,
    fetchUsageInfo,
  } = useAiSoapTemplateGeneration()

  // モーダル状態
  const [aiModalOpen, setAiModalOpen] = useState(false)

  // Toast機能
  const { showToast } = useToast()
  const queryClient = useQueryClient()

  // 医薬品グループクエリ（TanStack Query）
  const {
    data: groupData,
    isLoading: groupLoading,
    error: groupError,
  } = useDrugGroupsQuery({
    page,
    search: searchKeyword,
    enabled: activeTab === 0,
  })
  const drugGroups = groupData ?? null

  // totalPages を useMemo で計算（派生状態）
  const totalPages = useMemo(() => {
    if (groupData && activeTab === 0) {
      const pageSize = groupData.page_size || 10
      const totalPagesCount = Math.ceil(groupData.count / pageSize)
      return totalPagesCount > 0 ? totalPagesCount : 1
    }
    return 1
  }, [groupData, activeTab])

  // URLパラメータから高度検索情報を解析（メモ化）
  const urlSearchParam = searchParams.get('search') || ''
  const { isAdvanced: urlIsAdvanced, conditions: urlConditions } = useMemo(
    () => parseAdvancedSearchFromUrl(urlSearchParam),
    [urlSearchParam]
  )

  // 高度検索フック（医薬品タブ用 - TanStack Query）
  const {
    normalSearchKeyword,
    setNormalSearchKeyword,
    advancedConditions,
    searchResults,
    isLoading: advancedLoading,
    error: advancedError,
    executeNormalSearch,
    executeAdvancedSearch,
    currentPage: advancedCurrentPage,
    setCurrentPage: setAdvancedCurrentPage,
    isAdvancedMode,
    setIsAdvancedMode,
  } = useDrugSearchQuery({
    initialSearchKeyword: urlIsAdvanced ? '' : urlSearchParam,
  })

  // タブの定義
  const tabs = [
    { id: 'groups', label: '医薬品グループ' },
    { id: 'drugs', label: '医薬品' },
  ]

  // クエリパラメータ更新
  const updateQueryParams = useCallback(
    (tab: string, searchQuery = '', pageNum = 1) => {
      const params = new URLSearchParams()
      params.set('tab', tab)
      if (searchQuery) params.set('search', searchQuery)
      if (pageNum > 1) params.set('page', pageNum.toString())

      navigate({
        pathname: '/portal/soaps',
        search: params.toString(),
      })
    },
    [navigate]
  )

  // レガシーfetchDrugs関数を削除 - 高度検索フックに統一

  // URLパラメータから高度検索条件を復元
  useEffect(() => {
    if (activeTab === 1 && urlIsAdvanced && urlConditions.length > 0) {
      // useEffect内で実行する処理を関数として抽出
      const restoreAdvancedSearch = () => {
        // 高度検索モードに設定
        setIsAdvancedMode(true)
        // 高度検索を実行
        executeAdvancedSearch(urlConditions)
      }

      restoreAdvancedSearch()
    }
  }, [
    activeTab,
    urlSearchParam,
    setIsAdvancedMode,
    executeAdvancedSearch,
    urlIsAdvanced,
    urlConditions,
  ])

  // グループ一覧の総ページ数を計算（TanStack Query のデータから）
  // useMemo で計算するため、useEffect は不要

  // 検索処理（グループタブのみ）
  // TanStack Query が page/searchKeyword の変更を検知して自動的に再フェッチする
  const handleSearch = () => {
    setPage(1)
    if (activeTab === 0) {
      updateQueryParams(tabs[activeTab].id, searchKeyword, 1)
    }
    // 医薬品タブは高度検索フックで管理
  }

  // 高度検索用のハンドラー
  const handleAdvancedNormalSearch = useCallback(() => {
    executeNormalSearch()
    updateQueryParams('drugs', normalSearchKeyword, 1)
  }, [executeNormalSearch, normalSearchKeyword, updateQueryParams])

  const handleAdvancedSearch = useCallback(
    (conditions: AdvancedSearchCondition[]) => {
      executeAdvancedSearch(conditions)
      // 高度検索の場合は検索条件の概要をURLパラメータに含める
      const summary = conditions
        .filter((c) => c.value.trim())
        .map((c) => `${c.type}:${c.value}`)
        .join('+')
      updateQueryParams('drugs', `[高度]${summary}`, 1)
    },
    [executeAdvancedSearch, updateQueryParams]
  )

  const handleAdvancedPageChange = useCallback(
    (newPage: number) => {
      setAdvancedCurrentPage(newPage)
      // 現在の検索条件で新しいページを検索
      if (normalSearchKeyword) {
        executeNormalSearch(normalSearchKeyword, newPage)
      }
      updateQueryParams('drugs', normalSearchKeyword, newPage)
    },
    [
      setAdvancedCurrentPage,
      normalSearchKeyword,
      executeNormalSearch,
      updateQueryParams,
    ]
  )

  // タブ切り替え処理
  const handleTabChange = (index: number) => {
    setActiveTab(index)
    setPage(1)

    // タブ切り替え時に検索キーワードをクリアする
    // これにより、タブ間で検索条件が引き継がれることを防止する
    setSearchKeyword('')

    // 検索キーワードを空にしてURLパラメータを更新
    updateQueryParams(tabs[index].id, '', 1)
  }

  // ページ変更処理
  const handlePageChange = (newPage: number) => {
    setPage(newPage)
    updateQueryParams(tabs[activeTab].id, searchKeyword, newPage)
  }

  // 統一された表示データインターフェース（医薬品タブ用）
  const displayData = isAdvancedMode
    ? {
        drugs: searchResults?.results || [],
        loading: advancedLoading,
        error: advancedError?.message ?? null,
        currentPage: advancedCurrentPage,
        totalPages: searchResults
          ? Math.ceil(searchResults.count / (searchResults.page_size || 20))
          : 1,
        hasSearch:
          normalSearchKeyword.trim() !== '' || advancedConditions.length > 0,
      }
    : {
        drugs: searchResults?.results || [],
        loading: advancedLoading,
        error: advancedError?.message ?? null,
        currentPage: advancedCurrentPage,
        totalPages: searchResults
          ? Math.ceil(searchResults.count / (searchResults.page_size || 20))
          : 1,
        hasSearch: normalSearchKeyword.trim() !== '',
      }

  // 詳細ボタンクリック処理
  const handleDetailClick = (id: string) => {
    if (activeTab === 0) {
      navigate(`/portal/soaps/groups/${id}`)
    } else {
      navigate(`/portal/soaps/drugs/${id}`)
    }
  }

  // 新規グループボタンクリック処理
  const handleCreateGroup = () => {
    navigate('/portal/soaps/groups/create')
  }

  // AI生成モーダルを開く
  const handleAiGenerate = async () => {
    await fetchUsageInfo()
    setAiModalOpen(true)
  }

  // AI生成実行
  const handleAiGenerateExecute = async (requests: AiGenerationRequest[]) => {
    try {
      const response = await executeGeneration(requests)

      if (response) {
        // 生成成功の場合、グループ一覧キャッシュを無効化して再取得
        await queryClient.invalidateQueries({
          queryKey: queryKeys.drugGroups.all,
        })

        // AIモーダルを閉じる
        setAiModalOpen(false)

        // 成功メッセージを表示
        showToast('SOAPテンプレートを生成しました', 'success')
      }
    } catch (err) {
      const error = err as { type?: string }
      // 重複検知エラーの場合はダイアログを表示
      if (error.type === 'DUPLICATE_DETECTION') {
        setAiModalOpen(false) // AIモーダルを閉じて重複ダイアログを表示
        return // エラーを再throwしない
      }

      // その他のエラーの場合
      showToast('SOAPテンプレートの生成に失敗しました', 'error')
    }
  }

  // 重複確認ダイアログ: 既存グループにマージ
  const handleDuplicateMerge = async () => {
    try {
      const response = await executeMerge()

      if (response) {
        // 生成成功の場合、グループ一覧キャッシュを無効化して再取得
        await queryClient.invalidateQueries({
          queryKey: queryKeys.drugGroups.all,
        })

        // 成功時はダイアログを閉じる
        handleSuccess()
      }
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'SOAPテンプレートの生成に失敗しました'
      showToast(message, 'error')
    }
  }

  // 重複確認ダイアログ: 新しい名前で作成
  const handleDuplicateRename = async (newRequests: AiGenerationRequest[]) => {
    try {
      const response = await executeRename(newRequests)

      if (response) {
        // 生成成功の場合、グループ一覧キャッシュを無効化して再取得
        await queryClient.invalidateQueries({
          queryKey: queryKeys.drugGroups.all,
        })

        // 成功時はダイアログを閉じる
        handleSuccess()
      }
    } catch (err) {
      const error = err as { type?: string }
      // 新しい名前でも重複する場合は、フック内で処理される
      if (error.type !== 'DUPLICATE_DETECTION') {
        // その他のエラーの場合のみここで処理
        const message =
          err instanceof Error
            ? err.message
            : 'SOAPテンプレートの生成に失敗しました'
        showToast(message, 'error')

        // エラー時はダイアログを閉じる
        handleDuplicateCancel()
      }
    }
  }

  // 成功時の共通処理（マージ・リネーム成功時）
  const handleSuccess = () => {
    showToast('SOAPテンプレートを生成しました', 'success')
  }

  // 重複確認ダイアログ: キャンセル
  const handleDuplicateCancel = () => {
    cancelGeneration()
  }

  return (
    <PortalLayout>
      <PortalPageHeader title="SOAPテンプレート管理" />
      <Paper elevation={2} sx={{ p: 3 }}>
        {/* タブ切り替え */}
        <Box sx={{ mb: 3 }}>
          <TabSwitcher
            tabs={tabs}
            activeTab={tabs[activeTab].id}
            onTabChange={(tabId) => {
              const index = tabs.findIndex((tab) => tab.id === tabId)
              handleTabChange(index)
            }}
          />
        </Box>
        {/* 検索・フィルター部分（医薬品グループタブのみ） */}
        {activeTab === 0 && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'center' },
              mb: 3,
              gap: 2,
            }}
          >
            <SearchBox
              placeholder="グループ名で検索"
              value={searchKeyword}
              onChange={setSearchKeyword}
              onSearch={handleSearch}
            />
          </Box>
        )}

        {/* エラー表示 */}
        {(activeTab === 0 ? groupError : displayData.error) && (
          <Alert severity="error" sx={{ mb: 3 }}>
            データの取得中にエラーが発生しました。しばらくしてから再度お試しください。
          </Alert>
        )}

        {/* ローディング状態 */}
        {(activeTab === 0 ? groupLoading : displayData.loading) && (
          <Box sx={{ my: 4 }}>
            <LoadingIndicator />
          </Box>
        )}

        {/* テーブル部分 */}
        {!(activeTab === 0 ? groupLoading : displayData.loading) && (
          <Box sx={{ mb: 3, minHeight: 400 }}>
            {activeTab === 0 ? (
              <DrugGroupTable
                drugGroups={drugGroups?.results || []}
                loading={groupLoading}
                onViewDetails={handleDetailClick}
                onAddGroup={handleCreateGroup}
                onAiGenerate={handleAiGenerate}
              />
            ) : (
              <EnhancedDrugTable
                mode="drugs"
                data={displayData.drugs}
                loading={displayData.loading}
                error={displayData.error}
                normalSearchTerm={normalSearchKeyword}
                onNormalSearchChange={setNormalSearchKeyword}
                onNormalSearch={handleAdvancedNormalSearch}
                onAdvancedSearch={handleAdvancedSearch}
                currentPage={displayData.currentPage}
                totalPages={displayData.totalPages}
                onPageChange={handleAdvancedPageChange}
                onViewDetails={handleDetailClick}
                currentSearchKeyword={normalSearchKeyword}
                enableAdvancedSearch={true}
                searchPlaceholder="医薬品名またはYJコードで検索"
                isAdvancedMode={isAdvancedMode}
                onAdvancedModeChange={setIsAdvancedMode}
                initialAdvancedConditions={
                  urlIsAdvanced ? urlConditions : advancedConditions
                }
              />
            )}
          </Box>
        )}

        {/* ページネーション（医薬品グループタブのみ） */}
        {activeTab === 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <PaginationControl
              currentPage={page}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </Box>
        )}
      </Paper>

      {/* AI生成モーダル */}
      <AiSoapTemplateGenerationModal
        open={aiModalOpen}
        onClose={() => setAiModalOpen(false)}
        onGenerate={handleAiGenerateExecute}
        usageInfo={aiState.usageInfo}
        loading={aiGenerating}
        error={aiState.error}
      />

      {/* 重複確認ダイアログ */}
      {isDuplicateDetected && aiState.duplicateData && (
        <DuplicateConfirmationDialog
          open={isDuplicateDetected}
          existingGroups={aiState.duplicateData.existing_groups}
          originalRequests={aiState.requests}
          loading={aiGenerating}
          onMerge={handleDuplicateMerge}
          onRename={handleDuplicateRename}
          onCancel={handleDuplicateCancel}
        />
      )}
    </PortalLayout>
  )
}

export default SoapTemplateListPage
